import React from 'react';
import { motion } from 'framer-motion';
import { HandshakeIcon } from 'lucide-react';
import { TeamMember } from '../../data/teamData';

interface TalentCardProps {
  member: TeamMember;
  isClaimed: boolean;
  claimedNickname?: string;
  isClaimedByMe: boolean;
  hasMyClaim: boolean;
  handshakeRecord?: { similarity: number };
  onClickClaim: () => void;
  onClickHandshake: () => void;
}

const TalentCard: React.FC<TalentCardProps> = ({
  member,
  isClaimed,
  claimedNickname,
  isClaimedByMe,
  hasMyClaim,
  handshakeRecord,
  onClickClaim,
  onClickHandshake,
}) => {
  const showHandshakeBtn = hasMyClaim && isClaimed && !isClaimedByMe && !handshakeRecord;

  return (
    <motion.div
      className="bg-white rounded-2xl shadow-md hover:shadow-xl transition-shadow duration-300 p-5 flex flex-col"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -2 }}
    >
      <div className="flex items-center justify-between mb-3">
        {isClaimed ? (
          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-700">
            <span className="w-2 h-2 rounded-full bg-[#3A7D78]" />
            {claimedNickname}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-xs text-gray-400 bg-gray-100 px-2.5 py-1 rounded-full">
            待认领
          </span>
        )}

        {showHandshakeBtn && (
          <button
            onClick={(e) => { e.stopPropagation(); onClickHandshake(); }}
            className="flex items-center gap-1 px-3 py-1.5 bg-[#F5B6C1] text-white text-sm font-medium rounded-full hover:bg-[#e8a0ab] transition-colors"
          >
            <HandshakeIcon size={14} />
            握手
          </button>
        )}

        {handshakeRecord && (
          <span className="text-xs font-bold text-[#3A7D78] bg-[#3A7D78]/10 px-2.5 py-1 rounded-full">
            {handshakeRecord.similarity >= 0 ? '+' : ''}{handshakeRecord.similarity}%
          </span>
        )}

        {isClaimedByMe && (
          <span className="text-xs text-[#3A7D78] bg-[#3A7D78]/10 px-2.5 py-1 rounded-full font-medium">
            我的卡片
          </span>
        )}
      </div>

      {!isClaimed ? (
        <button
          onClick={onClickClaim}
          className="w-full text-left"
        >
          <div className="grid grid-cols-2 gap-1">
            {member.top10.map((t, i) => (
              <span
                key={t}
                className="text-xs text-gray-500 bg-gray-50 rounded-lg px-2 py-1.5 truncate"
              >
                <span className="text-gray-300 mr-1">{i + 1}.</span>
                {t}
              </span>
            ))}
          </div>
          <p className="text-center text-xs text-gray-300 mt-3">点击认领</p>
        </button>
      ) : isClaimedByMe ? (
        <div
          className="cursor-pointer"
          onClick={onClickHandshake}
        >
          <div className="grid grid-cols-2 gap-1">
            {member.top10.map((t, i) => (
              <span
                key={t}
                className="text-xs text-[#3A7D78]/80 bg-[#3A7D78]/5 rounded-lg px-2 py-1.5 truncate font-medium"
              >
                <span className="text-gray-300 mr-1 font-normal">{i + 1}.</span>
                {t}
              </span>
            ))}
          </div>
          <p className="text-center text-xs text-[#3A7D78] mt-3 font-medium">
            点击查看完整才干
          </p>
        </div>
      ) : handshakeRecord ? (
        <div
          className="cursor-pointer"
          onClick={onClickHandshake}
        >
          <div className="grid grid-cols-2 gap-1">
            {member.top10.map((t, i) => (
              <span
                key={t}
                className="text-xs text-gray-600 bg-gray-50 rounded-lg px-2 py-1.5 truncate"
              >
                <span className="text-gray-300 mr-1">{i + 1}.</span>
                {t}
              </span>
            ))}
          </div>
          <p className="text-center text-xs text-[#3A7D78] mt-3 font-medium">
            点击查看完整才干
          </p>
        </div>
      ) : (
        <div>
          <div className="grid grid-cols-2 gap-1">
            {member.top10.map((t, i) => (
              <span
                key={t}
                className="text-xs text-gray-600 bg-gray-50 rounded-lg px-2 py-1.5 truncate"
              >
                <span className="text-gray-300 mr-1">{i + 1}.</span>
                {t}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="mt-auto pt-3">
        <p className="text-[10px] text-gray-300 text-center">
          ID: {member.id}
        </p>
      </div>
    </motion.div>
  );
};

export default TalentCard;
